import React, { useMemo } from 'react';
import EmptyData from 'components/Messages/EmptyData/EmptyData';
import { TableColumnInterface, TableSubRowColumnInterface } from 'components/Table/table.types';
import OverviewTable from 'components/Table/Table';
import { ActivityInterface, useActivitiesContext } from 'pages/Activities/activities.context';
import UpdateActivity from 'pages/Activities/UpdateActivity';
import CancelActivity from 'pages/Activities/CancelActivity';
import CommentOnActivity from 'pages/Activities/CommentOnActivity';
import { Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import {
  convertHTMLStringToPlainString,
  formatDatePickerDate,
  truncateText,
} from 'utils/formatting';
import DropdownMenu from 'components/DropdownMenu/DropdownMenu';
import ActivityFilters from 'pages/Activities/Table/Filters';
import { getActivityAssignmentStatus, getActivityStatus } from 'pages/Activities/utils';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';
import { ActivityAssignmentData } from 'hooks/activities/useQueryActivityAssignments';
import PermissionCheck from 'components/Abilities/PermissionCheck';
import { ActivityState } from '__generated__/graphql';
import CancelActivityAssignment from 'pages/Activities/ActivityAssignment/CancelActivityAssignment';
import CommentOnActivityAssignment from 'pages/Activities/ActivityAssignment/CommentOnActivityAssignment';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/system';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CustomIcon from 'components/Icon/CustomIcon';
import ProgressCircle from 'components/ProgressCircle/ProgressCircle';

const getCompletedStepCount = (assignment: ActivityAssignmentData) => {
  if (!assignment.steps) {
    return 0;
  }
  let completedStepCount = 0;

  assignment.steps.forEach(({ response }) => {
    if (response?.completedDate) {
      completedStepCount++;
    }
  });

  return completedStepCount;
};

const getCompletedAssignmentCount = (activity: ActivityInterface) => {
  if (!activity.activityAssignments) {
    return 0;
  }
  let completedAssignmentCount = 0;

  activity.activityAssignments.forEach((assignment) => {
    if (assignment.completedDate && assignment.state === ActivityState.Active) {
      completedAssignmentCount++;
    }
  });

  return completedAssignmentCount;
};

const getActiveAssignmentCount = (activity: ActivityInterface) => {
  if (!activity.activityAssignments) {
    return 0;
  }
  let activeAssignmentCount = 0;

  activity.activityAssignments.forEach((assignment) => {
    if (assignment.state === ActivityState.Active) {
      activeAssignmentCount++;
    }
  });

  return activeAssignmentCount;
};

type Props = {
  checkedRows: ActivityInterface[];
  setCheckedRows: React.Dispatch<React.SetStateAction<ActivityInterface[]>>;
};

const ActivitiesTable = ({ checkedRows, setCheckedRows }: Props): JSX.Element => {
  const navigate = useNavigate();
  const { activities } = useActivitiesContext();
  const { ability, userTenantId } = useAuthContext();

  const tableCols: TableColumnInterface<ActivityInterface>[] = useMemo(
    () => [
      {
        label: 'Name',
        value: (activity) => (
          <Link to={`/activities/${activity.id}`}>
            {truncateText(activity.subject || 'N/A', 30)}
          </Link>
        ),
        sortBy: 'subject',
        width: '15%',
      },
      {
        label: 'Description',
        value: (activity) =>
          convertHTMLStringToPlainString(convertHTMLStringToPlainString(activity.description)),
        sortBy: 'description',
        width: '15%',
      },
      {
        label: 'Campaign',
        value: (activity) => activity.campaign?.name ?? '',
        width: '15%',
      },
      {
        label: 'Status',
        value: (activity) => getActivityStatus(activity),
        sortBy: 'state',
        width: '10%',
      },
      {
        label: 'Due Date',
        value: (activity) => formatDatePickerDate(activity.dueDate),
        sortBy: 'dueDate',
        width: '10%',
      },
      {
        label: 'Completed',
        value: (activity) => {
          const completedCount = getCompletedAssignmentCount(activity);
          const activeCount = getActiveAssignmentCount(activity);
          return (
            <Box display='flex' alignItems='center'>
              <ProgressCircle
                value={(activeCount > 0 ? completedCount / activeCount : 0) * 100}
                sx={{ mr: 1.25 }}
              />
              {`${completedCount} of ${activeCount}`}
            </Box>
          );
        },
        width: '10%',
      },
      {
        label: '',
        align: 'right',
        value: (activity) => (
          <DropdownMenu
            items={[
              { label: <UpdateActivity activity={activity} text='Edit' /> },
              {
                label: <CommentOnActivity activity={activity} text='Comment on Activity' />,
                disabled:
                  activity.assignedUsers.length === 0 && activity.assignedUsers.length === 0,
              },
              { label: <CancelActivity activity={activity} text='Cancel' /> },
            ]}
          />
        ),
        width: '1rem',
      },
    ],
    []
  );

  const subCols: TableSubRowColumnInterface<ActivityAssignmentData, ActivityInterface>[] = useMemo(
    () => [
      {
        label: 'Assigned To',
        value: (assignment, activity) => (
          <Link to={`/activities/${activity.id}/assignments/${assignment.id}`}>
            {assignment.assignedUser
              ? truncateText(
                  `${assignment.assignedUser.firstName} ${assignment.assignedUser.lastName}`,
                  30
                )
              : 'Unassigned'}
          </Link>
        ),
        width: '15%',
      },
      {
        label: 'Store',
        value: (assignment) => assignment.store?.name,
        width: '15%',
      },
      {
        label: 'Status',
        value: (assignment, activity) => getActivityAssignmentStatus({ ...assignment, activity }),
        width: '15%',
      },
      {
        label: 'Completed Date',
        value: (assignment) => formatDatePickerDate(assignment.completedDate),
        width: '15%',
      },
      {
        label: 'Steps Completed',
        value: (assignment) => {
          const completedCount = getCompletedStepCount(assignment);
          const activeCount = assignment.steps?.length || 0;
          return (
            <Box display='flex' alignItems='center'>
              <ProgressCircle
                value={(activeCount > 0 ? completedCount / activeCount : 0) * 100}
                sx={{ mr: 1.25 }}
              />
              {`${completedCount} of ${activeCount}`}
            </Box>
          );
        },
        width: '15%',
      },
      {
        label: '',
        align: 'right',
        value: (assignment, activity) => (
          <DropdownMenu
            items={[
              {
                label: (
                  <CustomIcon
                    label='View Activity'
                    icon={<ViewModuleIcon />}
                    onClick={() =>
                      navigate(`/activities/${activity.id}/assignments/${assignment.id}`)
                    }
                  />
                ),
              },
              {
                label: (
                  <CommentOnActivityAssignment
                    activityAssignment={assignment}
                    text='Comment on Activity'
                  />
                ),
                disabled: !assignment.assignedUser,
              },
              {
                label: (
                  <CancelActivityAssignment
                    activity={activity}
                    activityAssignment={assignment}
                    text='Cancel'
                  />
                ),
              },
            ]}
          />
        ),
        width: '1rem',
      },
    ],
    []
  );

  const filteredActivities = useMemo(
    () =>
      activities.filter((activity) => {
        return (
          !activity.archived &&
          (hasPermissions(ability, 'read', 'Activity', { tenantId: userTenantId }) ||
            hasPermissions(ability, 'read', 'Activity', { activityId: activity.id }))
        );
      }),
    [ability, activities, userTenantId]
  );

  return (
    <>
      <ActivityFilters />
      <OverviewTable
        data={filteredActivities}
        columns={tableCols}
        tableTitle='Activities'
        checkbox={{
          checkedRows,
          onCheck: (rows) => setCheckedRows(rows),
        }}
        noDataMessageOverride={
          <EmptyData
            dataType='activity'
            action={
              <PermissionCheck permission={{ action: 'create', subject: 'Activity' }}>
                <Button>
                  <Link to={'/activities/create'}>Create Activity</Link>
                </Button>
              </PermissionCheck>
            }
          />
        }
        subRows={{
          subRowKey: 'activityAssignments',
          columns: subCols,
          showHeader: true,
          caslSubject: 'ActivityAssignment',
          caslConditionKey: 'activityAssignmentId',
        }}
      />
    </>
  );
};

export default ActivitiesTable;
